import { useMemo, useState } from 'react';

import { Center, Flex, Input, InputGroup, InputRightElement, Spinner } from '@chakra-ui/react';

import { PaginationState } from '@tanstack/react-table';

import {
  DropdownOption,
  FilterData,
  Filters,
  MultiSelectionData,
  SearchNormalOutline,
} from '@aslanpay/portal-common-lib';

import { RECONCILIATION_PAGE_SIZE_OPTIONS } from 'app/components/Reconciliations/ReconciliationPagedTable';
import ResolveFindingDialog from 'app/components/Reconciliations/ResolveFindingDialog';
import EmptyState from 'app/components/UI/EmptyState';
import { Table } from 'app/components/UI/Table/Table';

import { ReconciliationReportRowStatusValues } from 'app/constants/reconciliations';
import { defaultPageSize } from 'app/constants/table';

import useFetchReconciliationFindings from 'app/hooks/useFetchReconciliationFindings';

import { ReconciliationFindingAction, ReconciliationReportRow } from 'app/types/reconciliations';

import { formatFindingType } from 'app/utils/reconciliations';
import getReconciliationReportColumns from 'app/utils/table/reconciliationReport';

import type { Filter } from '@aslanpay/portal-common-lib';

const TYPE_FILTER = 'Problem';
const STATUS_FILTER = 'Status';
const COMPANY_FILTER = 'Company';

interface ReconciliationProblemsTabProps {
  rows: ReconciliationReportRow[];
  isRunning: boolean;
  hasReport: boolean;
  isLoadingReport: boolean;
  showCompanyColumn: boolean;
}

/** Shows a reconciliation run's saved report: every open problem when it finished, filtered client-side. */
function ReconciliationProblemsTab({
  rows,
  isRunning,
  hasReport,
  isLoadingReport,
  showCompanyColumn,
}: ReconciliationProblemsTabProps) {
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [companyFilter, setCompanyFilter] = useState<string[]>([]);
  const [activeFilter, setActiveFilter] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [findingToResolve, setFindingToResolve] = useState<
    { id: string; action: ReconciliationFindingAction } | undefined
  >();
  const [fixedFindingIds, setFixedFindingIds] = useState<Set<string>>(new Set());
  const [acceptedFindingIds, setAcceptedFindingIds] = useState<Set<string>>(new Set());

  const typeOptions: DropdownOption[] = [...new Set(rows.map((row) => row.type))]
    .sort()
    .map((type) => ({ value: type, text: formatFindingType(type) }));
  const statusOptions: DropdownOption[] = [...new Set(rows.map((row) => row.status))].map(
    (status) => ({
      value: status,
      text: ReconciliationReportRowStatusValues[status]?.value ?? status,
    })
  );
  const companyOptions: DropdownOption[] = [
    ...new Map(
      rows
        .filter((row) => row.companyId)
        .map((row) => [row.companyId as string, row.companyName || (row.companyId as string)])
    ),
  ]
    .map(([value, text]) => ({ value, text }))
    .sort((a, b) => a.text.localeCompare(b.text));
  const filters: Filter[] = [
    { name: TYPE_FILTER, type: 'category', data: typeOptions, selectedValues: typeFilter },
    { name: STATUS_FILTER, type: 'category', data: statusOptions, selectedValues: statusFilter },
    ...(showCompanyColumn
      ? [
          {
            name: COMPANY_FILTER,
            type: 'category',
            data: companyOptions,
            selectedValues: companyFilter,
          } as Filter,
        ]
      : []),
  ];

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter(
      (row) =>
        (typeFilter.length === 0 || typeFilter.includes(row.type)) &&
        (statusFilter.length === 0 || statusFilter.includes(row.status)) &&
        (companyFilter.length === 0 || companyFilter.includes(row.companyId ?? '')) &&
        (!query ||
          [row.individualName, row.individualExternalPayrollId, row.individualEmail]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(query)))
    );
  }, [companyFilter, rows, search, statusFilter, typeFilter]);
  const visibleRows = filteredRows.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);
  const visibleFindingIds = visibleRows
    .map((row) => row.findingId)
    .filter((findingId): findingId is string => !!findingId);
  const { data: visibleFindings } = useFetchReconciliationFindings(
    { ids: visibleFindingIds, page: 0, size: Math.max(visibleFindingIds.length, 1) },
    !isRunning && hasReport && visibleFindingIds.length > 0
  );
  const resolvedFindings = (visibleFindings?.list ?? []).filter((finding) => finding.resolvedAt);
  const markedFixedIds = new Set([
    ...fixedFindingIds,
    ...resolvedFindings.filter((finding) => finding.resolvedBy).map((finding) => finding.id),
  ]);
  const markedAcceptedIds = new Set([
    ...acceptedFindingIds,
    ...resolvedFindings.filter((finding) => finding.accepted).map((finding) => finding.id),
  ]);
  const resolvedFindingIds = new Set(resolvedFindings.map((finding) => finding.id));

  const handleFilterChange = (name: string, values: (string | number)[]) => {
    if (name === TYPE_FILTER) {
      setTypeFilter(values as string[]);
    } else if (name === COMPANY_FILTER) {
      setCompanyFilter(values as string[]);
    } else {
      setStatusFilter(values as string[]);
    }
    setPagination({ pageIndex: 0, pageSize });
  };

  const formatFilterValue = (_: string, filterValue: string | number, filterData: FilterData) =>
    (filterData as MultiSelectionData).find((option) => option.value === filterValue)?.text ||
    String(filterValue);

  if (isRunning) {
    return (
      <EmptyState text="This reconciliation is still running. Its report will appear here when it finishes." />
    );
  }

  if (!hasReport) {
    return <EmptyState text="No report for this run" />;
  }

  if (isLoadingReport) {
    return (
      <Center w="full" py="40px" data-testid="reconciliation-report-loading">
        <Spinner color="brand.purple" size="xl" />
      </Center>
    );
  }

  if (rows.length === 0) {
    return <EmptyState text="This run found no problems" />;
  }

  return (
    <Flex flexDir="column" gap="20px" w="full">
      <Flex gap="20px" align="center" wrap="wrap">
        <Filters
          activeFilter={activeFilter}
          displayFormatter={formatFilterValue}
          filters={filters}
          onFilterClose={() => setActiveFilter(undefined)}
          onFilterChange={handleFilterChange}
          onFilterClick={setActiveFilter}
        />
        <InputGroup maxW="344px" color="brand.darkGray">
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPagination({ pageIndex: 0, pageSize });
            }}
            type="text"
            placeholder="Search by person or payroll ID"
            data-testid="reconciliation-report-search"
          />
          <InputRightElement w="56px" h="full">
            <SearchNormalOutline boxSize="24px" />
          </InputRightElement>
        </InputGroup>
      </Flex>

      {filteredRows.length === 0 ? (
        <EmptyState text="No problems match these filters" />
      ) : (
        <Table
          columns={getReconciliationReportColumns(showCompanyColumn, {
            onResolve: (row) =>
              setFindingToResolve(
                row.findingId ? { id: row.findingId, action: 'resolve' } : undefined
              ),
            onAccept: (row) =>
              setFindingToResolve(
                row.findingId ? { id: row.findingId, action: 'accept' } : undefined
              ),
            fixedFindingIds: markedFixedIds,
            acceptedFindingIds: markedAcceptedIds,
            resolvedFindingIds,
          })}
          data={visibleRows}
          total={filteredRows.length}
          pageNumber={pageIndex}
          pageSize={pageSize}
          pageSizeOptions={RECONCILIATION_PAGE_SIZE_OPTIONS}
          onPaginationChange={setPagination}
          getRowId={(row, index) => row.findingId || `${row.type}-${index}`}
        />
      )}
      <ResolveFindingDialog
        findingId={findingToResolve?.id}
        action={findingToResolve?.action}
        onClose={() => setFindingToResolve(undefined)}
        onResolved={(findingId) =>
          findingToResolve?.action === 'accept'
            ? setAcceptedFindingIds((ids) => new Set(ids).add(findingId))
            : setFixedFindingIds((ids) => new Set(ids).add(findingId))
        }
      />
    </Flex>
  );
}

export default ReconciliationProblemsTab;
